import type { FC, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { UploadDataOutput } from "@aws-amplify/storage";

import clsx from "clsx";

import type { Accept } from "react-dropzone";
import { useDropzone } from "react-dropzone";

import CloseIcon from "assets/icons/filled/close-circle.svg?react";
import ImgIcon from "assets/icons/outlined/image.svg?react";
import UploadIcon from "assets/icons/outlined/upload.svg?react";

import { HiddenFileSelector } from "shared/Components";
import { useDebounce, useS3Uploader } from "shared/hooks";
import type { LogoModel, MediaModel } from "shared/types";
import { ImageSizesEnum, Img, Loader, Text } from "shared/uikit";

import colors from "theme/colors";

import { El, UploaderBlock } from "./style";

import ProgressBar from "../ProgressBar";
import { TextStylesEnum, TextVariantsEnum, UploaderSizesEnum } from "../types";

const acceptStyle = {
	borderColor: colors.palette.colors.green["500"],
	borderWidth: "2px"
};

const rejectStyle = {
	borderColor: colors.palette.colors.red["500"],
	borderWidth: "2px"
};

interface UploaderProps {
	src?: LogoModel | null | string;
	multiple?: boolean;
	onChange?: (uploadedFileInfo: null | MediaModel) => void;
	availableFileTypes?: Accept;
	size?: UploaderSizesEnum;
	icon?: ReactNode;
	showProgress?: boolean;
	hidePreview?: boolean;
	cancellable?: boolean;
	removable?: boolean;
	onChangeUploadingStatus?: (uploading: boolean) => void;
	square?: boolean;
	disabled?: boolean;
}

const Uploader: FC<UploaderProps> = ({
	src,
	multiple = false,
	availableFileTypes = { "image/*": [] },
	onChange,
	size = UploaderSizesEnum.Small240,
	icon,
	showProgress,
	hidePreview,
	cancellable = true,
	removable = false,
	onChangeUploadingStatus,
	square,
	disabled
}) => {
	const { uploadFile, cancelUpload } = useS3Uploader();

	const [openFileSelect, setOpenFileSelect] = useState(!src);
	const [uploading, setUploading] = useState(false);
	const [filesInfo, setFilesInfo] = useState<FileList>();
	const [logoSrc, setLogoSrc] = useState(src);
	const [uploader, setUploader] = useState<UploadDataOutput | null>();
	const [uploadProgress, setUploadProgress] = useState(0);
	const [remainingTime, setRemainingTime] = useState<number>();

	const debouncedRemaining = useDebounce(remainingTime, 250);

	useEffect(() => {
		onChangeUploadingStatus && onChangeUploadingStatus(uploading);
	}, [uploading, onChangeUploadingStatus]);

	const handleSelectFiles = useCallback(
		<T extends File>(acceptedFiles: T[] | FileList | null) => {
			setOpenFileSelect(false);

			if (acceptedFiles?.length) {
				setFilesInfo(acceptedFiles as FileList);
				const firstFile = acceptedFiles[0];

				setUploading(true);

				const totalSize = Array.from(acceptedFiles).reduce(
					(accumulator, currentValue) => accumulator + currentValue.size,
					0
				);
				const timeStarted = new Date().getTime();

				const upload = uploadFile({
					file: firstFile!,
					handleProgressUpdate: progress => {
						const now = new Date().getTime();
						const uploadedBytes = totalSize * progress;

						const timeElapsed = now - timeStarted;
						const uploadSpeed = uploadedBytes / (timeElapsed / 1000);

						setUploadProgress(progress * 100);
						setRemainingTime(Math.floor((totalSize - uploadedBytes) / uploadSpeed));
					},
					handleComplete: (uploadedFileInfo: MediaModel) => {
						if (uploadedFileInfo) {
							onChange && onChange(uploadedFileInfo);
						}
						!hidePreview && firstFile?.type?.includes("image") && setLogoSrc(window.URL.createObjectURL(firstFile!));
						setUploading(false);
					},
					handleError: () => {
						!hidePreview && setLogoSrc("");
						setUploading(false);
					}
				});
				setUploader(upload);
			}
		},
		[uploadFile, onChange, hidePreview]
	);

	const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
		accept: availableFileTypes,
		multiple,
		onDrop: handleSelectFiles,
		disabled: !openFileSelect
	});

	useEffect(() => {
		!hidePreview && setLogoSrc(src);
	}, [src, hidePreview]);

	const openFileDialog = () => {
		if (!disabled) {
			setOpenFileSelect(true);
		}
	};

	const rollbackUpload = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();

		if (uploading) {
			!hidePreview && setLogoSrc(src);
		} else {
			setLogoSrc(null);
			setOpenFileSelect(true);
			setFilesInfo(undefined);
			onChange && onChange(null);
		}

		setUploading(false);
		uploader && cancelUpload(uploader);
	};

	const removeUploaded = () => {
		setLogoSrc(null);
		setOpenFileSelect(true);
		setFilesInfo(undefined);
		onChange && onChange(null);
	};

	const uploaderStyle = useMemo(
		() => ({
			...(isDragAccept ? acceptStyle : {}),
			...(isDragReject ? rejectStyle : {})
		}),
		[isDragAccept, isDragReject]
	);

	const wrapperProps = useMemo(() => {
		return disabled ? {} : getRootProps({ style: uploaderStyle });
	}, [getRootProps, uploaderStyle, disabled]);

	return (
		<>
			<div
				className={clsx(
					"shrink-0 relative",
					disabled && "cursor-not-allowed",
					!logoSrc && "border border-dashed border-gray-200",
					size === UploaderSizesEnum.Small240
						? "w-[15rem] h-[15rem]"
						: size === UploaderSizesEnum.Rectangle420
							? "w-[420px] h-[180px]"
							: "w-[6rem] h-[6rem]",
					size === UploaderSizesEnum.Rectangle420 ? "rounded-xl" : "rounded-full",
					size === UploaderSizesEnum.Rectangle420 && "border-2 px-[70px]",
					square && "rounded-lg"
				)}
				{...wrapperProps}
			>
				{!uploading && (
					<>
						{logoSrc ? (
							<El className="z-20">
								<Img
									src={logoSrc}
									size={size === UploaderSizesEnum.Small240 ? ImageSizesEnum.Small240 : ImageSizesEnum.Small96}
									circle
									noBg={uploading}
								/>
							</El>
						) : !!filesInfo?.length && filesInfo[0]?.name ? (
							<div className="w-full h-full flex justify-center items-center">
								<Text
									variants={TextVariantsEnum.BodyLarge}
									style={TextStylesEnum.Hint}
									className="px-5 w-full text-center truncate"
								>
									{filesInfo[0].name}
								</Text>
							</div>
						) : (
							<UploaderBlock
								className="flex flex-col justify-center items-center"
								onClick={openFileDialog}
								disabled={disabled}
							>
								{icon ? (
									icon
								) : (
									<ImgIcon
										className={clsx(
											"svg-paths:stroke-gray-300",
											size === UploaderSizesEnum.Small96 ? "w-7 h-7" : "w-16 h-16"
										)}
									/>
								)}
								{size === UploaderSizesEnum.Small240 && (
									<Text variants={TextVariantsEnum.BodyMedium} style={TextStylesEnum.Inactive} className="mt-1">
										Max 20MB
									</Text>
								)}
								<Text
									variants={
										size === UploaderSizesEnum.Small96 ? TextVariantsEnum.BodySmall : TextVariantsEnum.BodyLarge
									}
									style={TextStylesEnum.Primary}
									className="mt-3.5 font-bold"
								>
									Upload
								</Text>
							</UploaderBlock>
						)}
						{removable && !!filesInfo?.length && (
							<div
								className={clsx(
									"absolute cursor-pointer z-20",
									size === UploaderSizesEnum.Small240 && "top-2.5 right-3",
									square && "-top-2.5 -right-2.5",
									size !== UploaderSizesEnum.Small240 && !square && "top-0 right-0"
								)}
								onClick={removeUploaded}
							>
								<CloseIcon
									className={clsx(
										"svg-first-path:fill-gray-400 svg-first-path:stroke-gray-400",
										size === UploaderSizesEnum.Small240 ? "w-10 h-10" : "w-6 h-6"
									)}
								/>
							</div>
						)}
					</>
				)}
				{(logoSrc || uploading) && cancellable && (
					<div
						className={clsx(
							"absolute cursor-pointer z-20",
							size === UploaderSizesEnum.Small240 && "top-2.5 right-3",
							square && "-top-2.5 -right-2.5",
							size !== UploaderSizesEnum.Small240 && !square && "top-0 right-0"
						)}
						onClick={rollbackUpload}
					>
						<CloseIcon
							className={clsx(
								"svg-first-path:fill-gray-400 svg-first-path:stroke-gray-400",
								size === UploaderSizesEnum.Small240 ? "w-10 h-10" : "w-6 h-6"
							)}
						/>
					</div>
				)}
				{uploading && (
					<>
						{showProgress ? (
							<div className="w-full h-full flex flex-col justify-center">
								<div className="flex justify-center items-center">
									<div className="mr-2">
										<UploadIcon className="w-6 h-6 svg-paths:stroke-gray-500" />
									</div>
									<Text variants={TextVariantsEnum.BodyMedium}>Uploading...</Text>
								</div>
								{!!filesInfo?.length && filesInfo[0]?.name && (
									<Text
										variants={TextVariantsEnum.BodySmall}
										style={TextStylesEnum.Hint}
										className="mt-2 text-center truncate"
									>
										{filesInfo[0].name}
									</Text>
								)}
								<div className="py-4">
									<ProgressBar value={uploadProgress} variant={uploadProgress ? "determinate" : "indeterminate"} />
								</div>
								{debouncedRemaining !== undefined && (
									<Text variants={TextVariantsEnum.Caption} style={TextStylesEnum.Inactive} className="text-center">
										{debouncedRemaining} sec. remaining
									</Text>
								)}
							</div>
						) : (
							<El className="flex justify-center items-center z-10 bg-white">
								<Loader className={clsx(size === UploaderSizesEnum.Small240 ? "w-40 h-40" : "w-[60px] h-[60px]")} />
							</El>
						)}
					</>
				)}
				{openFileSelect && !disabled && (
					<HiddenFileSelector
						open
						multiple={multiple}
						onSelect={handleSelectFiles}
						disabled={!openFileSelect}
						inputProps={getInputProps()}
						availableFileTypes={availableFileTypes}
					/>
				)}
			</div>
		</>
	);
};

export default Uploader;
